import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  DialogActions,
  Button,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Box,
} from '@mui/material';
import { upperFirst } from 'lodash-es';
import DraggableModal from '../../components/DraggableModal';
import { RowSpacing } from './RowSpacing';
import { selectors as growerSelectors } from '../../store/dux/grower-dux';
import appConfig from '../../config';

export function EditAttributes(props) {
  const { fieldsInfo, close, save } = props;
  const growerName = useSelector(growerSelectors.selectedName);

  const [record, setRecord] = useState({
    saveFieldName: false,
    fieldName: '',
    saveFarmName: false,
    farmName: '',
    savePlannedCrop: false,
    plannedCrop: '',
    saveRowSpacing: false,
    rowSpacing: '',
    saveIrrigated: false,
    irrigated: '',
    earlyFungicide: '',
    saveEarlyFungicide: false,
    lateFungicide: '',
    saveLateFungicide: false,
    tillageType: '',
    saveTillageType: false,
    coverCrop: '',
    saveCoverCrop: false,
    starterFertilizer: '',
    saveStarterFertilizer: false,
    traitPackage: '',
    saveTraitPackage: false,
  });

  const getChangeHandler = (prop) => (e) => {
    const saveProp = `save${upperFirst(prop)}`;
    const value = e.target.value;
    setRecord({ ...record, [prop]: value, [saveProp]: Boolean(value) });
  };

  const getCheckboxChangeHandler = (prop) => (e) => {
    const value = e.target.checked;
    setRecord({ ...record, [prop]: value });
  };

  return (
    <DraggableModal
      handleClose={close}
      title={
        <span>
          Bulk Edit Attributes for <b>{growerName}</b>
        </span>
      }
      noMinHeight={true}
    >
      <Box>
        <Checkbox
          checked={record.saveFieldName}
          onChange={getCheckboxChangeHandler('saveFieldName')}
        />
        <TextField
          variant="standard"
          label="Field Name"
          value={record.fieldName || ''}
          onChange={getChangeHandler('fieldName')}
        />
      </Box>
      <Box>
        <Checkbox
          checked={record.saveFarmName}
          onChange={getCheckboxChangeHandler('saveFarmName')}
        />
        <TextField
          variant="standard"
          label="Farm Name"
          value={record.farmName || ''}
          onChange={getChangeHandler('farmName')}
        />
      </Box>
      <Box>
        <Checkbox
          checked={record.savePlannedCrop}
          onChange={getCheckboxChangeHandler('savePlannedCrop')}
        />
        <FormControl variant="standard" sx={{ minWidth: 166 }}>
          <InputLabel id="plannedcrop">Planned Crop</InputLabel>
          <Select
            id="plannedcrop"
            value={record.plannedCrop || ''}
            onChange={getChangeHandler('plannedCrop')}
          >
            <MenuItem value="" key="none"></MenuItem>
            <MenuItem value="corn" key="corn">
              Corn
            </MenuItem>
            <MenuItem value="soy" key="soy">
              Soy
            </MenuItem>
            <MenuItem value="wheat" key="wheat">
              Wheat
            </MenuItem>
            <MenuItem value="other" key="other">
              Other
            </MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box>
        <Checkbox
          checked={record.saveRowSpacing}
          onChange={getCheckboxChangeHandler('saveRowSpacing')}
        />
        <RowSpacing
          value={record.rowSpacing || ''}
          onChange={getChangeHandler('rowSpacing')}
        />
      </Box>
      <Box>
        <Checkbox
          checked={record.saveIrrigated}
          onChange={getCheckboxChangeHandler('saveIrrigated')}
        />
        <FormControl variant="standard" sx={{ minWidth: 166 }}>
          <InputLabel id="irrigated">Irrigated</InputLabel>
          <Select
            id="irrigated"
            value={record.irrigated || ''}
            onChange={getChangeHandler('irrigated')}
          >
            <MenuItem value="" key="none"></MenuItem>
            <MenuItem value="yes" key="yes">
              Yes
            </MenuItem>
            <MenuItem value="no" key="no">
              No
            </MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box>
        <Checkbox
          checked={record.saveEarlyFungicide}
          onChange={getCheckboxChangeHandler('saveEarlyFungicide')}
        />
        <FormControl variant="standard" sx={{ minWidth: 166 }}>
          <InputLabel id="earlyFungicide">Early Fungicide</InputLabel>
          <Select
            id="earlyFungicide"
            value={record.earlyFungicide || ''}
            onChange={getChangeHandler('earlyFungicide')}
          >
            <MenuItem value="" key="none"></MenuItem>
            <MenuItem value="yes" key="yes">
              Yes
            </MenuItem>
            <MenuItem value="no" key="no">
              No
            </MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box>
        <Checkbox
          checked={record.saveLateFungicide}
          onChange={getCheckboxChangeHandler('saveLateFungicide')}
        />
        <FormControl variant="standard" sx={{ minWidth: 166 }}>
          <InputLabel id="lateFungicide">Late Fungicide</InputLabel>
          <Select
            id="lateFungicide"
            value={record.lateFungicide || ''}
            onChange={getChangeHandler('lateFungicide')}
          >
            <MenuItem value="" key="none"></MenuItem>
            <MenuItem value="yes" key="yes">
              Yes
            </MenuItem>
            <MenuItem value="no" key="no">
              No
            </MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box>
        <Checkbox
          checked={record.saveTillageType}
          onChange={getCheckboxChangeHandler('saveTillageType')}
        />
        <FormControl variant="standard" sx={{ minWidth: 166 }}>
          <InputLabel id="tillageType">Tillage Type</InputLabel>
          <Select
            id="tillageType"
            value={record.tillageType || ''}
            onChange={getChangeHandler('tillageType')}
          >
            <MenuItem value="" key="none"></MenuItem>
            {appConfig.tillageTypes.map(({ id, label }) => (
              <MenuItem key={id} value={id}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box>
        <Checkbox
          checked={record.saveCoverCrop}
          onChange={getCheckboxChangeHandler('saveCoverCrop')}
        />
        <FormControl variant="standard" sx={{ minWidth: 166 }}>
          <InputLabel id="coverCrop">Cover Crop</InputLabel>
          <Select
            id="coverCrop"
            value={record.coverCrop || ''}
            onChange={getChangeHandler('coverCrop')}
          >
            <MenuItem value="" key="none"></MenuItem>
            <MenuItem value="yes" key="yes">
              Yes
            </MenuItem>
            <MenuItem value="no" key="no">
              No
            </MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box>
        <Checkbox
          checked={record.saveStarterFertilizer}
          onChange={getCheckboxChangeHandler('saveStarterFertilizer')}
        />
        <FormControl variant="standard" sx={{ minWidth: 166 }}>
          <InputLabel id="starterFertilizer">Starter Fertilizer</InputLabel>
          <Select
            id="starterFertilizer"
            value={record.starterFertilizer || ''}
            onChange={getChangeHandler('starterFertilizer')}
          >
            <MenuItem value="" key="empty"></MenuItem>
            <MenuItem value="None" key="none">
              None
            </MenuItem>
            <MenuItem value="2x2" key="2x2">
              2x2
            </MenuItem>
            <MenuItem value="On Top" key="ontop">
              On Top
            </MenuItem>
            <MenuItem value="In-Furrow" key="in-furrow">
              In-Furrow
            </MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box>
        <Checkbox
          checked={record.saveTraitPackage}
          onChange={getCheckboxChangeHandler('saveTraitPackage')}
        />
        <FormControl variant="standard" sx={{ minWidth: 166 }}>
          <InputLabel id="traitPackage">Trait Package</InputLabel>
          <Select
            id="traitPackage"
            value={record.traitPackage || ''}
            onChange={getChangeHandler('traitPackage')}
          >
            <MenuItem value="" key="none"></MenuItem>
            <MenuItem value="GMO" key="gmo">
              GMO
            </MenuItem>
            <MenuItem value="Non-GMO" key="non-gmo">
              Non-GMO
            </MenuItem>
          </Select>
        </FormControl>
      </Box>
      <DialogActions>
        <Button
          color="inherit"
          variant="outlined"
          onClick={(e) => {
            save(fieldsInfo, record);
            close(e);
          }}
        >
          Save
        </Button>
        <Button onClick={close} color="inherit" variant="outlined">
          Close
        </Button>
      </DialogActions>
    </DraggableModal>
  );
}
